import clsx from "clsx";
import gsap from "gsap";
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { TitleWithIconType } from "@shared/content-types/types";
import { getTitleWithIconData } from "@shared/ui/typography/apply-replacements-to-text";

import s from "./main-title.module.scss";

export type MainTitleProps = ComponentProps<"div"> & {
  className?: string;
  titles: TitleWithIconType[];
};

export const MainTitle = (props: MainTitleProps) => {
  const { className, titles } = props;

  const textRef = useRef<HTMLDivElement[] | null[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [activeIndex, setActiveIndex] = useState(0);

  const titlesSetting = useMemo(() => {
    return titles.map((title) => getTitleWithIconData(title));
  }, [titles]);

  const titlesTexts = useMemo(
    () => titlesSetting.map((item) => item.text.split(" ")),
    [titlesSetting],
  );

  const handleNext = useCallback(() => {
    setActiveIndex((prevIndex) => (prevIndex + 1) % titlesTexts.length);
  }, []);

  useEffect(() => {
    const currentText = textRef.current[activeIndex];
    const letters = currentText?.querySelectorAll(`.${s.char}`);
    const icons = currentText?.querySelectorAll(`.${s.icon}`);

    if (currentText && letters && icons) {
      const tl = gsap
        .timeline()
        .set(currentText, {
          opacity: 1,
          ease: "none",
          duration: 0.01,
        })
        .fromTo(
          letters,
          {
            display: "none",
          },
          {
            display: "inline-block",
            duration: 0.01,
            ease: "none",
            stagger: 0.1,
          },
        )
        .from(
          icons,
          {
            opacity: 0,
            duration: 1,
            scale: 0.8,
            rotate: 45,
            stagger: 0.5,
            ease: "power4.inOut",
          },
          "+=0.3",
        )
        .to(
          currentText,
          {
            opacity: 0,
            y: 100,
            duration: 1,
            ease: "power4.inOut",
          },
          "+=5",
        )
        .add(handleNext)
        .set(currentText, { clearProps: "all" })
        .set(letters, { clearProps: "all" });

      return () => {
        tl.kill();
      };
    }
  }, [activeIndex]);

  const content = useMemo(() => {
    return titlesTexts.map((words, titleIndex) => {
      let letterIndex = 1;

      return words.map((word, wordIndex) => {
        const letters = word.split("").map((char, charIndex) => {
          const currentIndex = letterIndex;

          const icon = titlesSetting[titleIndex].settings[currentIndex];

          letterIndex++;

          return (
            <span
              key={`word-${wordIndex}_char-${charIndex + 1}`}
              className={s.char}
              data-index={currentIndex}
            >
              {char}
              {icon && <div className={s.icon}>{icon}</div>}
            </span>
          );
        });

        return (
          <span
            key={`word_${wordIndex + 1}`}
            className="word-wrapper"
            style={{ display: "inline-block" }}
          >
            {letters}
            {wordIndex < words.length - 1 && "\u00A0"}{" "}
          </span>
        );
      });
    });
  }, [titlesTexts]);

  return (
    <div className={clsx(s.root, className)}>
      <div className={s.titleWrapper} ref={containerRef}>
        {content.map((text, index) => (
          <div
            className={s.title}
            key={`title_${index + 1}`}
            ref={(ref) => {
              textRef.current[index] = ref;
            }}
          >
            {text}
            <div className={s.line} />
          </div>
        ))}
      </div>
    </div>
  );
};

MainTitle.displayName = "MainTitle";
